import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart as PieChartIcon, AlertCircle, Ban } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getCallEndings } from '@/api/analytics/api';
import type { CallEndingsResponse } from '@/api/analytics/types';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface CallEndingsChartProps {
    className?: string;
    startDate?: string;
    endDate?: string;
    rubricId?: string;
    keyPath?: string;
    assistantId?: string;
    onError?: (error: string) => void;
}

// Professional color palette for call ending reasons
const REASON_COLORS: Record<string, string> = {
    'assistant-ended-call': 'hsl(var(--primary))',
    'assistant-error': 'hsl(0, 84%, 60%)',
    'assistant-not-found': 'hsl(25, 95%, 53%)',
    'db-error': 'hsl(0, 72%, 51%)',
    'pipeline-error-openai-voice-failed': 'hsl(346, 87%, 43%)',
    'silence-timed-out': 'hsl(45, 93%, 47%)',
    'customer-ended-call': 'hsl(142, 71%, 45%)',
    'customer-did-not-answer': 'hsl(217, 91%, 60%)',
    'customer-did-not-give-microphone-permission': 'hsl(262, 83%, 58%)',
    'vonage-disconnected': 'hsl(198, 85%, 45%)',
    'vonage-failed-to-connect-call': 'hsl(280, 76%, 58%)',
    'phone-call-provider-bypass-enabled-but-no-call-received': 'hsl(174, 75%, 37%)',
};

// Base palette of 16 truly distinct colors (maximum visual difference)
// Carefully selected to be as different as possible from each other
const BASE_DISTINCT_COLORS = [
    'hsl(221, 83%, 53%)',   // Blue
    'hsl(25, 95%, 53%)',    // Orange  
    'hsl(142, 71%, 45%)',   // Green
    'hsl(340, 82%, 52%)',   // Pink/Red
    'hsl(262, 83%, 58%)',   // Purple
    'hsl(48, 96%, 53%)',    // Yellow
    'hsl(180, 77%, 47%)',   // Cyan/Aqua
    'hsl(14, 86%, 57%)',    // Coral
    'hsl(280, 87%, 65%)',   // Magenta
    'hsl(160, 84%, 39%)',   // Emerald/Teal
    'hsl(33, 100%, 50%)',   // Amber/Gold
    'hsl(199, 89%, 48%)',   // Sky Blue
    'hsl(330, 81%, 60%)',   // Rose
    'hsl(120, 61%, 50%)',   // Lime
    'hsl(271, 76%, 53%)',   // Indigo
    'hsl(349, 89%, 60%)',   // Crimson
];

// Generate color with maximum contrast
const generateDistinctColor = (index: number, totalReasons: number): string => {
    // For small numbers (≤16), use base palette directly - truly unique colors
    if (totalReasons <= BASE_DISTINCT_COLORS.length) {
        return BASE_DISTINCT_COLORS[index % BASE_DISTINCT_COLORS.length];
    }
    
    // For medium numbers (17-36), use base palette with lightness variation
    if (totalReasons <= 36) {
        const baseIndex = index % BASE_DISTINCT_COLORS.length;
        const layer = Math.floor(index / BASE_DISTINCT_COLORS.length);
        const lightnesses = [50, 42, 58]; // Medium, Dark, Light
        
        // Extract HSL values from base color
        const baseColor = BASE_DISTINCT_COLORS[baseIndex];
        const hslMatch = baseColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        if (hslMatch) {
            const hue = hslMatch[1];
            const saturation = hslMatch[2];
            const newLightness = lightnesses[layer];
            return `hsl(${hue}, ${saturation}%, ${newLightness}%)`;
        }
        return baseColor;
    }
    
    // For large numbers (37+), use Golden Angle with layered S/L variation
    const goldenAngle = 137.508;
    const hue = (index * goldenAngle) % 360;
    
    const layer = Math.floor(index / 12);
    const saturations = [75, 85, 65, 70, 80];
    const lightnesses = [50, 45, 55, 48, 52];
    
    const saturation = saturations[layer % saturations.length];
    const lightness = lightnesses[layer % lightnesses.length];
    
    return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
};

// Generate color with maximum contrast to adjacent segments
const getReasonColor = (reason: string, index: number, totalReasons: number): string => {
    // If predefined color exists, use it
    if (REASON_COLORS[reason]) {
        return REASON_COLORS[reason];
    }
    
    // Generate distinct color based on total number of reasons
    return generateDistinctColor(index, totalReasons);
};

const LoadingSkeleton = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex-1 flex flex-col space-y-4 min-h-0"
    >
        {/* Chart Skeleton - flex-1 to match actual chart height */}
        <div className="flex-1 relative rounded-lg border bg-gradient-to-br from-muted/30 via-background to-background px-4 pt-4 pb-2 flex flex-col min-h-0">
            {/* Subtle Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
                backgroundSize: '24px 24px'
            }} />
            
            <div className="relative flex-1 flex flex-col justify-around gap-6">
                {/* 3 horizontal bars matching actual chart */}
                {[75, 85, 70].map((width, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-3 w-14 shrink-0" /> {/* Y-axis label */}
                        <Skeleton 
                            className="h-[60px] rounded-lg" 
                            style={{ width: `${width}%` }}
                        />
                    </div>
                ))}
            </div>
        </div>
        
        {/* Legend Skeleton - Matching exact structure (shrink-0 prevents growth) */}
        <div className="rounded-lg border bg-muted/20 shrink-0 overflow-hidden">
            <div className="px-3 pb-0.5 border-b bg-muted/30">
                <Skeleton className="h-3 w-24" />
            </div>
            <div className="max-h-[100px] overflow-y-auto">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 p-3">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                            <Skeleton className="h-2 w-2 rounded-full shrink-0" />
                            <Skeleton className="h-2.5 flex-1" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </motion.div>
);

const EmptyState = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="flex flex-col items-center justify-center h-[300px]"
    >
        <div className="relative">
            <div className="absolute inset-0 bg-primary/5 blur-2xl rounded-full" />
            <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl border-2 border-dashed border-primary/20 bg-background">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/10 to-transparent rounded-2xl" />
                <div className="relative">
                    <PieChartIcon className="w-7 h-7 text-primary/50" />
                </div>
            </div>
        </div>
        
        <div className="mt-6 text-center space-y-2">
            <div className="text-sm font-medium text-muted-foreground">No Call Endings Data</div>
            <div className="text-xs text-muted-foreground/80 max-w-[250px]">
                There are currently no calls recorded in the selected time period to analyze ending distributions.
            </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
            <motion.div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10"
                whileHover={{ scale: 1.05, backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                transition={{ duration: 0.2 }}
            >
                <Ban className="w-3.5 h-3.5 text-primary/50" />
                <span className="text-xs font-medium text-primary/50">No Total</span>
            </motion.div>
            <motion.div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10"
                whileHover={{ scale: 1.05, backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                transition={{ duration: 0.2 }}
            >
                <AlertCircle className="w-3.5 h-3.5 text-primary/50" />
                <span className="text-xs font-medium text-primary/50">No Inbound/Outbound</span>
            </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 opacity-[0.08]"
                style={{
                    background: `
                        linear-gradient(45deg, transparent 0%, hsl(var(--primary)) 100%),
                        linear-gradient(-45deg, transparent 0%, hsl(var(--primary)) 100%)
                    `,
                    maskImage: 'radial-gradient(circle at center, transparent 50%, black 150%)'
                }}
            />
        </div>
    </motion.div>
);

export const CallEndingsChart = ({
    className,
    startDate,
    endDate,
    rubricId,
    keyPath,
    assistantId,
    onError
}: CallEndingsChartProps) => {
    const [data, setData] = useState<CallEndingsResponse['data'] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorState, setErrorState] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setErrorState(null);

            try {
                const params: any = {
                    startDate,
                    endDate,
                };
                if (rubricId) params.rubricId = rubricId;
                if (keyPath) params.keyPath = keyPath;
                if (assistantId) params.assistantId = assistantId;
                
                const response = await getCallEndings(params);

                if (response.data) {
                    setData(response.data.data);
                } else if (response.error) {
                    throw new Error(response.error);
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch data';
                setErrorState(message);
                onError?.(message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate, rubricId, keyPath, assistantId]);

    const hasNoData = !isLoading && !errorState && (!data ||
        (data.overall.total === 0 && data.inboundCalls.total === 0 && data.outboundCalls.total === 0));

    // Build chart data and config
    const { chartData, chartConfig, sortedReasons } = useMemo(() => {
        if (!data) return { chartData: [], chartConfig: {}, sortedReasons: [] };

        // Get all unique reasons with their total counts
        const reasonTotals = new Map<string, number>();
        data.overall.byReason.forEach(r => {
            reasonTotals.set(r.reason, (reasonTotals.get(r.reason) || 0) + r.count);
        });
        data.inboundCalls.byReason.forEach(r => {
            if (!reasonTotals.has(r.reason)) {
                reasonTotals.set(r.reason, r.count);
            }
        });
        data.outboundCalls.byReason.forEach(r => {
            if (!reasonTotals.has(r.reason)) {
                reasonTotals.set(r.reason, r.count);
            }
        });

        // Sort reasons by total count (largest first)
        const allSortedReasons = Array.from(reasonTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([reason]) => reason);
        
        // Reorder: Biggest first, second biggest last, rest in between
        const sortedReasons = allSortedReasons.length >= 2 
            ? [
                allSortedReasons[0],                           // Biggest (leftmost)
                ...allSortedReasons.slice(2),                  // Middle segments (sorted)
                allSortedReasons[1]                            // Second biggest (rightmost)
            ]
            : allSortedReasons; // If less than 2 segments, keep as is

        // Build chart config in sorted order
        const config: Record<string, { label: string; color: string }> = {};
        const totalReasons = sortedReasons.length;
        sortedReasons.forEach((reason, index) => {
            config[reason] = {
                label: reason.toLowerCase().replace(/_/g, ' '),
                color: getReasonColor(reason, index, totalReasons),
            };
        });

        // Build chart data (3 bars: Total, Inbound, Outbound)
        const chartData = [
            {
                type: 'Total',
                ...data.overall.byReason.reduce((acc, r) => ({ ...acc, [r.reason]: r.count }), {}),
            },
            {
                type: 'Inbound',
                ...data.inboundCalls.byReason.reduce((acc, r) => ({ ...acc, [r.reason]: r.count }), {}),
            },
            {
                type: 'Outbound',
                ...data.outboundCalls.byReason.reduce((acc, r) => ({ ...acc, [r.reason]: r.count }), {}),
            },
        ];

        return { chartData, chartConfig: config, sortedReasons };
    }, [data]);

    if (errorState) {
        return (
            <Card className={cn("relative pt-8 overflow-visible shadow-sm", className)}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="relative flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-full blur-lg" />
                        <div className="relative bg-background/95 backdrop-blur-sm px-6 py-2 rounded-full border shadow-sm flex items-center gap-2">
                            <PieChartIcon className="h-4 w-4 text-primary/60" />
                            <span className="font-medium text-xs">Call Endings Distribution</span>
                        </div>
                    </div>
                </div>
                <CardContent className="p-5">
                    <div className="flex flex-col items-center justify-center h-[200px] text-center">
                        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
                        <p className="text-sm text-destructive">{errorState}</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={cn("relative pt-8 overflow-visible shadow-sm h-full flex flex-col", className)}>
            {/* Floating Header Badge */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="relative flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-full blur-lg" />
                    <div className="relative bg-background/95 backdrop-blur-sm px-6 py-2 rounded-full border shadow-sm flex items-center gap-2">
                        <PieChartIcon className="h-4 w-4 text-primary/60" />
                        <span className="font-medium text-xs">Call Endings Distribution</span>
                    </div>
                </div>
            </div>

            <CardContent className="px-5 pt-3 pb-5 flex-1 flex flex-col">
                <AnimatePresence mode="wait">
                    {isLoading ? (
                        <LoadingSkeleton />
                    ) : hasNoData ? (
                        <EmptyState />
                    ) : data ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="flex-1 flex flex-col space-y-4 min-h-0"
                        >
                            {/* Stacked Bar Chart */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.1 }}
                                className="relative rounded-lg border bg-gradient-to-br from-muted/30 via-background to-background px-4 pt-4 pb-2 flex-1 flex flex-col min-h-0"
                            >
                                {/* Subtle Background Pattern */}
                                <div className="absolute inset-0 opacity-[0.03]" style={{
                                    backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
                                    backgroundSize: '24px 24px'
                                }} />

                                <div className="relative flex-1 min-h-0">
                                    <ChartContainer config={chartConfig} className="h-full w-full">
                                        <BarChart 
                                            accessibilityLayer 
                                            data={chartData}
                                            layout="vertical"
                                            margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                                            barSize={60}
                                        >
                                            {/* Gradient Definitions for each reason */}
                                            <defs>
                                                {Object.entries(chartConfig).map(([key, config]) => {
                                                    const gradientId = `gradient-${key}`;
                                                    return (
                                                        <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="1" y2="0">
                                                            <stop offset="0%" stopColor={config.color} stopOpacity={0.8} />
                                                            <stop offset="100%" stopColor={config.color} stopOpacity={0.5} />
                                                        </linearGradient>
                                                    );
                                                })}
                                            </defs>

                                            <CartesianGrid 
                                                strokeDasharray="3 3" 
                                                horizontal={false}
                                                stroke="hsl(var(--border))"
                                                strokeOpacity={0.3}
                                            />
                                            <XAxis
                                                type="number"
                                                tickLine={false}
                                                axisLine={{
                                                    stroke: 'hsl(var(--border))',
                                                    strokeWidth: 1,
                                                    strokeOpacity: 0.3
                                                }}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                                tickMargin={8}
                                            />
                                            <YAxis
                                                dataKey="type"
                                                type="category"
                                                tickLine={false}
                                                axisLine={{
                                                    stroke: 'hsl(var(--border))',
                                                    strokeWidth: 1,
                                                    strokeOpacity: 0.3
                                                }}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12, fontWeight: 500 }}
                                                tickMargin={8}
                                            />
                                            <ChartTooltip 
                                                content={({ active, payload }) => {
                                                    if (!active || !payload || !payload.length) return null;
                                                    
                                                    const data = payload[0].payload;
                                                    const callType = data.type;
                                                    
                                                    // Filter out zero values and sort by count (descending)
                                                    const validEntries = payload
                                                        .filter((entry: any) => entry.value > 0)
                                                        .sort((a: any, b: any) => b.value - a.value);
                                                    
                                                    const totalCount = validEntries.reduce((sum: number, entry: any) => sum + entry.value, 0);
                                                    
                                                    return (
                                                        <div 
                                                            className="rounded-lg border-2 bg-background shadow-lg overflow-hidden min-w-[280px] max-w-[350px]"
                                                            style={{ pointerEvents: 'auto' }}
                                                        >
                                                            {/* Header - Sticky */}
                                                            <div className="sticky top-0 bg-muted/50 px-3 py-2 border-b z-10 backdrop-blur-sm">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="font-semibold text-sm">{callType} Calls</span>
                                                                    <span className="text-xs text-muted-foreground">Total: {totalCount}</span>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* All Entries - Scrollable */}
                                                            <div 
                                                                className="px-3 py-2.5 space-y-1.5 max-h-[300px] overflow-y-auto"
                                                                style={{ 
                                                                    scrollbarWidth: 'thin',
                                                                    scrollbarColor: 'hsl(var(--muted-foreground)) transparent'
                                                                }}
                                                            >
                                                                {validEntries.map((entry: any, index: number) => {
                                                                    const reason = entry.dataKey;
                                                                    const label = chartConfig[reason]?.label || reason;
                                                                    const color = chartConfig[reason]?.color || entry.fill;
                                                                    const percentage = ((entry.value / totalCount) * 100).toFixed(1);
                                                                    
                                                                    return (
                                                                        <div key={index} className="flex items-start justify-between gap-3 text-xs">
                                                                            <div className="flex items-start gap-2 min-w-0 flex-1">
                                                                                <div 
                                                                                    className="h-2.5 w-2.5 rounded-full flex-shrink-0 mt-0.5" 
                                                                                    style={{ backgroundColor: color }}
                                                                                />
                                                                                <span className="text-muted-foreground capitalize break-words leading-tight">
                                                                                    {label}
                                                                                </span>
                                                                            </div>
                                                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                                <span className="font-semibold">{entry.value}</span>
                                                                                <span className="text-[10px] text-muted-foreground">({percentage}%)</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }}
                                                wrapperStyle={{ pointerEvents: 'auto' }}
                                            />
                                            {sortedReasons.map((key, index) => {
                                                const isFirst = index === 0;
                                                const isLast = index === sortedReasons.length - 1;
                                                
                                                // For horizontal bars: [topLeft, topRight, bottomRight, bottomLeft]
                                                // First segment: round left side [6, 0, 0, 6]
                                                // Last segment: round right side [0, 6, 6, 0]
                                                // Middle segments: no rounding [0, 0, 0, 0]
                                                let radius: [number, number, number, number] = [0, 0, 0, 0];
                                                if (isFirst) {
                                                    radius = [6, 0, 0, 6]; // Round left side
                                                } else if (isLast) {
                                                    radius = [0, 6, 6, 0]; // Round right side
                                                }
                                                
                                                return (
                                                    <Bar
                                                        key={key}
                                                        dataKey={key}
                                                        stackId="a"
                                                        fill={`url(#gradient-${key})`}
                                                        radius={radius}
                                                        animationDuration={800}
                                                        animationEasing="ease-out"
                                                        minPointSize={2}
                                                    />
                                                );
                                            })}
                                        </BarChart>
                                    </ChartContainer>
                                </div>
                            </motion.div>

                            {/* Reason Legend (Scrollable Grid) */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="rounded-lg border bg-muted/20 shrink-0 overflow-hidden"
                            >
                                <div className="px-3 pb-0.5 border-b bg-muted/30">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground leading-none">
                                        Ending Reasons
                                    </span>
                                </div>
                                <div className="max-h-[100px] overflow-y-auto">
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 p-3">
                                        {Object.entries(chartConfig).map(([key, config]) => (
                                            <div key={key} className="flex items-center gap-1.5">
                                                <div 
                                                    className="h-2 w-2 rounded-full flex-shrink-0" 
                                                    style={{ backgroundColor: config.color }}
                                                />
                                                <span className="text-[10px] text-muted-foreground capitalize truncate">
                                                    {config.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </CardContent>
        </Card>
    );
};
